import { IPostRequest } from "csi/comm/PostRequest";
import ExtendedPath from "csi/core/ExtendedPath";
import Spec from "csi/core/Spec";
import Key from "csi/core/Key";
import { getLocalizedGroupLabel } from "csi/store/common";
import xml from "csi/xml";
import declare from "dojo/_base/declare";
import _ from "lodash";
import { IAggregateStoreGroup } from "src/c8-platform/type/aggregateStore";
import { C8Node, C8NodeType } from "src/c8-platform/type/node";
import { VALUE_TYPE } from "src/c8-platform/type/value";
import StoreNodesConstructor from "./Nodes";
import { StoreQuery, StoreQueryOptions } from "./Request";
import StoreValuesConstructor, { StoreValues, ValuesResult, ValuesResultFromServer } from "./Values";

/** convert aggregate groups to values */
function convertGroupsToValues(groups: IAggregateStoreGroup[]): ValuesResult[] {
	return groups.map((group) => {
		const { results } = group;
		const _count = results.length;
		const _refs = results.map(({ $id }) => $id);
		const _text = getLocalizedGroupLabel(group);
		const _textCount = `${_text} (${_count})`;

		return {
			_count,
			_refs,
			_suggested: group.suggested,
			_text,
			_textCount,
			_value: group.groupId,
		};
	});
}

/** return key to use for grouping aggregate store groups */
export function getAggregateGroupKey(nodeType: C8NodeType): Key {
	return new Key({
		extendedPath: ExtendedPath.create(nodeType),
		prop: xml.PROP_TYPE,
	});
}

export interface IAggregateGroupValues extends StoreValues {
	suggestedProperty: string;
}

export const AggregateGroupValues = declare(StoreValuesConstructor, {
	/** @property {IAggregateStoreGroup[]} aggregateStoreGroups */
	aggregateStoreGroups: null,

	/** @property {ValuesResult[]} _aggregateResults */
	_aggregateResults: null,

	/** @property {string} suggestedProperty */
	suggestedProperty: "_suggested",

	/** @override use values from aggregate store response if there is no query or sort conditions */
	getRequest(query?: StoreQuery, options?: StoreQueryOptions, bReport?: boolean, bForce?: boolean): IPostRequest | Promise<any> {
		if (this.isDefaultAggregateQuery(query, options)) {
			return new Promise((resolve, reject) => {
				resolve(this._getAggregateResults());
			});
		}

		return this.inherited<IPostRequest>(arguments);
	},

	/** return true if there are no filters or sort conditions */
	isDefaultAggregateQuery(query?: StoreQuery, options?: StoreQueryOptions): boolean {
		const { filterQuery, sortQuery } = this;

		return this.isAggregateGroupStore() &&
			_.isEmpty(query) &&
			_.isEmpty(options?.sort) &&
			(filterQuery?.isEmpty() ?? true) &&
			(sortQuery?.isEmpty() ?? true);
	},

	/** return true if this values store is used for grouping */
	isAggregateGroupStore(): boolean {
		return this.spec.heading.m_prop === xml.PROP_TYPE;
	},

	/** @override group results from query by aggregate store groups */
	_convertToData(resultValues: ValuesResultFromServer[], resultNodes: C8Node[]): void {
		if (this.isAggregateGroupStore()) {
			const setUrls = new Set(resultValues?.[0]?.Refs || []);
			const originalResults = this._getAggregateResults();
			const filteredResults = originalResults.map((result) => {
				const filteredRefs = result._refs.filter((url) => setUrls.has(url));

				return {
					...result,
					_count: filteredRefs.length,
					_refs: filteredRefs,
				};
			});

			this.bHasRefs = true;
			this._setData(filteredResults);
		} else {
			this.inherited(arguments, [resultValues, resultNodes]);
		}
	},

	/** calculate results from aggregate groups */
	_getAggregateResults(): ValuesResult[] {
		this._aggregateResults ||= convertGroupsToValues(this.aggregateStoreGroups);
		return this._aggregateResults;
	},

	/** @override bypass default store behavior if a default query as the data will already be properly formatted */
	_onRequestData(query: StoreQuery, options: StoreQueryOptions, data: object[]): void {
		if (this.isDefaultAggregateQuery(query, options)) {
			const values = data as ValuesResult[];
			this._indexData(values);
			this._setData(values);
			this._resolveQueries();
		} else {
			this.inherited(arguments);
		}
	},
});

const AggregateGroupNodes = declare(StoreNodesConstructor, {
	valuesClass: AggregateGroupValues,

	/** @property {IAggregateStoreGroup[]} aggregateStoreGroups - aggregate store groups containing nodes */
	aggregateStoreGroups: null,

	/**
	 * @override
	 * Forward groups to values store
	 */
	getValuesStore(spec: Spec, storeOptions: Record<string, any>): StoreValues {
		const { aggregateStoreGroups } = this;

		storeOptions = {
			...storeOptions,
			aggregateStoreGroups,
		};

		return this.inherited<StoreValues>(arguments, [spec, storeOptions]);
	},
});

export default AggregateGroupNodes;